import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HistoryIcon from '@mui/icons-material/History';
import CancelIcon from '@mui/icons-material/Cancel';
import MeasurementsTab from '../execution/MeasurementsTab';
import type { Measurement } from '../../hooks/useExecutionStatus';
import {
  useTestRunsQuery,
  useEquipmentRunsQuery,
  useCancelTestRun
} from '../../hooks/useTestRuns';
import { useTranslation } from 'react-i18next';
import { useCalibrationRuntimeByGuid } from '@features/calibration/hooks/useCalibrationRuntime/useCalibrationRuntimeRead';
import type { BasicReadingTestPoint } from '@features/calibration/hooks/useCalibrationRuntime/types';
import type { TestRun } from '../../types';

const STATUS_COLORS: Record<
  string,
  'default' | 'success' | 'error' | 'warning' | 'info'
> = {
  PENDING: 'default',
  RUNNING: 'info',
  PAUSED: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  ABORTED: 'warning'
};

const RESULT_COLORS: Record<string, 'success' | 'error' | 'default'> = {
  PASS: 'success',
  FAIL: 'error'
};

type EventLogEntry = {
  timestamp: string;
  level: string;
  message: string;
};

type TestPointEntry = {
  name: string;
  measuredAsFound: number | null;
  measuredAsLeft: number | null;
  measuredAsFoundText: string | null;
  measuredAsLeftText: string | null;
  measuredAsFoundAt: string | null;
  measuredAsLeftAt: string | null;
  unitAsFound: string | null;
  unitAsLeft: string | null;
  imageIdAsFound: string | null;
  imageIdAsLeft: string | null;
};

type PerEquipmentBucket = {
  cell: string | null;
  equipmentGlobalId: string | null;
  measurements: {
    testPoint: string;
    measured: number | null;
    measuredText: string | null;
    unit: string | null;
    imageId: string | null;
  }[];
};

const toPassFail = (
  result: 'Pass' | 'Fail' | null | undefined
): Measurement['passFail'] =>
  result === 'Pass' ? 'PASS' : result === 'Fail' ? 'FAIL' : null;

const benchPointEntries = (tp: BasicReadingTestPoint): Measurement[] => {
  const entries: Measurement[] = [];
  if (tp.asFound != null) {
    entries.push({
      testPoint: tp.id,
      nominal: tp.nominalValue,
      measured: tp.asFound,
      measuredText: tp.asFoundText ?? null,
      unit: tp.asFoundUnit ?? null,
      error: null,
      passFail: toPassFail(tp.asFoundResult),
      imageId: tp.asFoundImageId ?? null
    });
  }
  if (tp.asLeft != null) {
    entries.push({
      testPoint: tp.id,
      nominal: tp.nominalValue,
      measured: tp.asLeft,
      measuredText: tp.asLeftText ?? null,
      unit: tp.asLeftUnit ?? null,
      error: null,
      passFail: toPassFail(tp.asLeftResult),
      imageId: tp.asLeftImageId ?? null
    });
  }
  if (entries.length === 0) {
    entries.push({
      testPoint: tp.id,
      nominal: tp.nominalValue,
      measured: null,
      measuredText: null,
      unit: null,
      error: null,
      passFail: null,
      imageId: null
    });
  }
  return entries;
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

const formatDuration = (start: string | null, end: string | null) => {
  if (!start || !end) return '-';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
};

type Props = {
  scriptId: string;
  equipmentGlobalId?: string;
};

const RunDetail = ({
  run,
  scriptId,
  expanded
}: {
  run: TestRun;
  scriptId: string;
  expanded: boolean;
}) => {
  const isBench = !!run.dcRuntimeId;
  const { calibrationData: benchData, isLoading: isBenchLoading } =
    useCalibrationRuntimeByGuid(
      run.dcRuntimeId ?? undefined,
      isBench && expanded
    );

  const eventLog = useMemo<EventLogEntry[]>(() => {
    if (!run.eventLog) return [];
    try {
      return JSON.parse(run.eventLog);
    } catch {
      return [];
    }
  }, [run.eventLog]);

  const measurements = useMemo<Measurement[]>(() => {
    if (isBench) {
      const points = benchData?.tests?.basicReading?.testPoints ?? [];
      return (points as BasicReadingTestPoint[]).flatMap(benchPointEntries);
    }
    if (!run.measurementResults) return [];
    try {
      const parsed = JSON.parse(run.measurementResults);
      // Work-order per-equipment buckets (VOLTBOT-507)
      if (parsed.perEquipment && Array.isArray(parsed.perEquipment)) {
        const entries: Measurement[] = [];
        for (const bucket of parsed.perEquipment as PerEquipmentBucket[]) {
          for (const m of bucket.measurements ?? []) {
            entries.push({
              testPoint: bucket.cell
                ? `${bucket.cell} — ${m.testPoint}`
                : m.testPoint,
              nominal: null,
              measured: m.measured ?? null,
              measuredText: m.measuredText ?? null,
              unit: m.unit ?? null,
              error: null,
              passFail: null,
              imageId: m.imageId ?? null
            });
          }
        }
        return entries;
      }
      // New flat testPoints format
      if (parsed.testPoints && Array.isArray(parsed.testPoints)) {
        const entries: Measurement[] = [];
        for (const tp of parsed.testPoints as TestPointEntry[]) {
          if (tp.measuredAsFound != null) {
            entries.push({
              testPoint: tp.name,
              nominal: null,
              measured: tp.measuredAsFound,
              measuredText: tp.measuredAsFoundText ?? null,
              unit: tp.unitAsFound ?? null,
              error: null,
              passFail: null,
              imageId: tp.imageIdAsFound ?? null
            });
          }
          if (tp.measuredAsLeft != null) {
            entries.push({
              testPoint: tp.name,
              nominal: null,
              measured: tp.measuredAsLeft,
              measuredText: tp.measuredAsLeftText ?? null,
              unit: tp.unitAsLeft ?? null,
              error: null,
              passFail: null,
              imageId: tp.imageIdAsLeft ?? null
            });
          }
          // Show entry even if no measurements yet
          if (tp.measuredAsFound == null && tp.measuredAsLeft == null) {
            entries.push({
              testPoint: tp.name,
              nominal: null,
              measured: null,
              measuredText: null,
              unit: null,
              error: null,
              passFail: null,
              imageId: null
            });
          }
        }
        return entries;
      }
      // Legacy flat array format
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return [];
    }
  }, [run.measurementResults, isBench, benchData]);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {measurements.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={600} gutterBottom>
            Measurements ({measurements.length})
          </Typography>
          <MeasurementsTab
            measurements={measurements}
            scriptId={scriptId}
            runId={run.id}
          />
        </Box>
      )}

      {eventLog.length > 0 && (
        <Box>
          <Typography variant="caption" fontWeight={600} gutterBottom>
            Event Log ({eventLog.length})
          </Typography>
          <List
            dense
            sx={{
              maxHeight: 250,
              overflow: 'auto',
              py: 0,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              bgcolor: 'grey.50',
              borderRadius: 1
            }}
          >
            {eventLog.map((evt, i) => (
              <ListItem key={i} sx={{ py: 0.15, px: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    mr: 1,
                    flexShrink: 0
                  }}
                >
                  {new Date(evt.timestamp).toLocaleTimeString()}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    color:
                      evt.level === 'error'
                        ? 'error.main'
                        : evt.level === 'success'
                          ? 'success.main'
                          : evt.level === 'warning'
                            ? 'warning.main'
                            : 'text.primary'
                  }}
                >
                  {evt.message}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {eventLog.length === 0 && measurements.length === 0 && (
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {isBench && isBenchLoading
            ? 'Loading calibration record...'
            : 'No detailed data available for this run.'}
        </Typography>
      )}
    </Box>
  );
};

export const HistoryTab = ({ scriptId, equipmentGlobalId }: Props) => {
  const { t } = useTranslation();
  const isEquipmentMode = !!equipmentGlobalId;
  const scriptRuns = useTestRunsQuery(isEquipmentMode ? '' : scriptId);
  const equipmentRuns = useEquipmentRunsQuery(equipmentGlobalId ?? null);
  const { data, isLoading } = isEquipmentMode ? equipmentRuns : scriptRuns;
  const cancelTestRun = useCancelTestRun(scriptId);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const runs = data?.data ?? [];

  const toggleExpand = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  if (runs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {t('scripts.noRuns', 'No test runs yet')}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {t(
            'scripts.noRunsHint',
            'Start a test run from the Test tab to see history here.'
          )}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {runs.map(run => (
        <Paper key={run.id} variant="outlined">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              gap: 2,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            onClick={() => toggleExpand(run.id)}
          >
            <IconButton size="small">
              {expandedId === run.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500} noWrap>
                {run.id.substring(0, 8)}... &mdash; {run.startedBy}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(run.createdAt)}
                {run.startedAt && run.completedAt && (
                  <>
                    {' '}
                    &middot; Duration:{' '}
                    {formatDuration(run.startedAt, run.completedAt)}
                  </>
                )}
              </Typography>
            </Box>
            <Chip
              label={run.status}
              size="small"
              color={STATUS_COLORS[run.status] ?? 'default'}
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
            {(run.status === 'PENDING' || run.status === 'RUNNING') && (
              <Tooltip title="Cancel run">
                <IconButton
                  size="small"
                  color="error"
                  onClick={e => {
                    e.stopPropagation();
                    cancelTestRun.mutate(run.id);
                  }}
                  disabled={cancelTestRun.isPending}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {run.overallResult && (
              <Chip
                label={run.overallResult}
                size="small"
                color={RESULT_COLORS[run.overallResult] ?? 'default'}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <Collapse in={expandedId === run.id}>
            <RunDetail
              run={run}
              scriptId={scriptId}
              expanded={expandedId === run.id}
            />
          </Collapse>
        </Paper>
      ))}
    </Box>
  );
};
